import React, { useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Dialog, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { Transition } from "../../UiComponents/Transition/Transition";
import { PostClientWalletTopup, PostClientWalletRemove } from "../../Service/_index";
import Styles from "./ClientCompanyWallet.module.scss";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;

const WalletTransactionDialog = ({ open, mode, company, clientId, clientName, currentBalance, onClose, onSaved }) => {
  const isRemove = mode === "remove";
  const fileInputRef = useRef(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptError, setReceiptError] = useState("");

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError("Amount must be a number")
      .positive("Amount must be greater than 0")
      .max(10000000, "Amount looks too large — please check")
      .test("two-decimals", "Maximum 2 decimal places", (v) =>
        v === undefined || v === null || /^\d+(\.\d{1,2})?$/.test(String(v))
      )
      .test("within-balance", "Amount exceeds the available balance", (v) => {
        if (!isRemove || v == null) return true;
        return v <= Number(currentBalance || 0);
      })
      .required("Amount is required"),
    remarks: isRemove
      ? Yup.string()
          .trim()
          .min(3, "Please provide a reason for removing the amount")
          .max(200, "Remarks cannot exceed 200 characters")
          .required("Please provide a reason for removing the amount")
      : Yup.string().trim().max(200, "Remarks cannot exceed 200 characters"),
  });

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_RECEIPT_BYTES) {
      setReceiptError("File is too large — 5MB max");
      return;
    }
    setReceiptError("");
    setReceiptFile(file);
  };

  const clearFile = () => {
    setReceiptFile(null);
    setReceiptError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    clearFile();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{ sx: { maxWidth: "480px !important", width: "100%", borderRadius: "10px" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
        className={Styles.DialogHeader}
      >
        <Box>
          <Typography className={Styles.DialogTitle}>
            {isRemove ? "Remove Amount" : "Topup Wallet"}
          </Typography>
          <Typography className={Styles.DialogSubtitle}>
            {clientName} &middot; {company}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "white" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Formik
        initialValues={{ amount: "", remarks: "" }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          const amount = Number(values.amount);
          const remarks = (values.remarks || "").trim();
          let request;

          if (isRemove) {
            request = PostClientWalletRemove({ clientId, companyName: company, amount, remarks });
          } else {
            const formData = new FormData();
            formData.append("clientId", clientId);
            formData.append("companyName", company);
            formData.append("amount", amount);
            formData.append("remarks", remarks);
            if (receiptFile) formData.append("receiptImage", receiptFile);
            request = PostClientWalletTopup(formData);
          }

          request
            .then((res) => {
              if (res && !res.error) {
                clearFile();
                onSaved();
              } else if (res?.message) {
                setFieldError("amount", res.message);
              }
            })
            .catch(() => {})
            .finally(() => setSubmitting(false));
        }}
      >
        {({ values, errors, touched, isSubmitting }) => {
          const amountNum = Number(values.amount || 0);
          const afterBalance = isRemove
            ? Number(currentBalance || 0) - amountNum
            : Number(currentBalance || 0) + amountNum;

          return (
            <Form>
              <Box sx={{ display: "flex", flexDirection: "column" }} className={Styles.DialogBody}>
                <Box
                  sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", mb: "14px" }}
                  className={Styles.PanelBalanceRow}
                >
                  <span>Current: &#8377; {money(currentBalance)}</span>
                  <span>&rarr;</span>
                  <span
                    className={`${Styles.PanelBalanceAfter} ${
                      isRemove ? Styles.PanelBalanceAfterDown : Styles.PanelBalanceAfterUp
                    }`}
                  >
                    After: &#8377; {money(values.amount ? afterBalance : currentBalance)}
                  </span>
                </Box>

                <Typography className={Styles.FieldLabel}>Amount</Typography>
                <Field
                  name="amount"
                  type="number"
                  autoFocus
                  className={Styles.InputFiled}
                  placeholder="Enter amount"
                />
                {touched.amount && errors.amount && (
                  <Typography className={Styles.FieldError}>{errors.amount}</Typography>
                )}

                <Typography className={Styles.FieldLabel} sx={{ mt: 2 }}>
                  Remarks {!isRemove && "(optional)"}
                </Typography>
                <Field
                  name="remarks"
                  as="textarea"
                  rows={2}
                  className={Styles.InputFiled}
                  style={{ resize: "vertical", height: "auto", fontFamily: "inherit" }}
                  placeholder={isRemove ? "Reason for removing the amount" : "Optional note"}
                />
                {touched.remarks && errors.remarks && (
                  <Typography className={Styles.FieldError}>{errors.remarks}</Typography>
                )}

                {!isRemove && (
                  <>
                    <Typography className={Styles.FieldLabel} sx={{ mt: 2 }}>
                      Payment Receipt (optional)
                    </Typography>
                    {receiptFile ? (
                      <Box
                        sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}
                        className={Styles.ReceiptChip}
                      >
                        <UploadFileIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: 12, flex: 1 }} noWrap>
                          {receiptFile.name}
                        </Typography>
                        <IconButton size="small" onClick={clearFile}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          textTransform: "none",
                          fontSize: 12.5,
                          borderRadius: "6px",
                          borderColor: "#D0D5DD",
                          color: "#344054",
                          width: "fit-content",
                        }}
                      >
                        Attach screenshot / receipt
                        <input
                          ref={fileInputRef}
                          type="file"
                          hidden
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                        />
                      </Button>
                    )}
                    {receiptError && <Typography className={Styles.FieldError}>{receiptError}</Typography>}
                  </>
                )}
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }}
                className={Styles.DialogFooter}
              >
                <Button
                  variant="outlined"
                  sx={{ textTransform: "none", borderRadius: "6px" }}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={isRemove ? Styles.RemoveButton : Styles.TopupButton}
                  variant={isRemove ? "outlined" : "contained"}
                  disableElevation
                  startIcon={
                    isRemove ? (
                      <RemoveIcon sx={{ fontSize: "16px !important" }} />
                    ) : (
                      <AddIcon sx={{ fontSize: "16px !important" }} />
                    )
                  }
                  disabled={isSubmitting}
                >
                  {isRemove ? "Remove Amount" : "Topup"}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default WalletTransactionDialog;
